import { initAdminUserApi } from "@/api/services/admin/admin-user";
import { useMe } from "@/api/services/admin/auth";
import { fetchOrganizationDetailApi } from "@/api/services/admin/organization";
import { Button } from "@/components/elements/Button";
import { Input } from "@/components/elements/Input";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { Modal } from "@/components/elements/Modal";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { FCC } from "@/types/common";
import { showError } from "@/utils/error";
import { createUrlWithParams, openNewTab } from "@/utils/url";
import { Box, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { FC, useEffect, useRef, useState } from "react";

type Props = {
  isOpen: boolean;
  organizationId?: string;
  productContractId?: number | string;
  onClose: () => void;
};

const MIN_PROCESSING_MS = 5000;

export const FirstLoginModal: FC<Props> = ({ isOpen, organizationId, productContractId, onClose }) => {
  const { data: me } = useMe();
  const { data: orgData, isLoading: isLoadingOrg } = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_DETAIL, organizationId],
    queryFn: () => fetchOrganizationDetailApi(organizationId as string),
    enabled: isOpen && Boolean(organizationId),
  });

  const organization = orgData?.data;
  const contract = organization?.productContracts.find((item) => String(item.id) === String(productContractId));

  const [isProcessing, setIsProcessing] = useState(false);
  const hasAutoRunRef = useRef(false);

  // Provision the product admin account first; only once that succeeds is the tab sent to the SSO
  // redirect page, which mints the (60s, single-use) redirect right before navigating.
  const handleSetup = async (withDelay = false) => {
    const productCode = contract?.product?.code;
    if (!productCode || !organizationId) return;

    setIsProcessing(true);
    const delayTime = withDelay ? MIN_PROCESSING_MS : 0;
    const [succeeded] = await Promise.all([
      initAdminUserApi({ organizationId: Number(organizationId), productCode })
        .then(() => true)
        .catch((error) => {
          showError(error);
          return false;
        }),
      new Promise((resolve) => setTimeout(resolve, delayTime)),
    ]);
    setIsProcessing(false);
    if (succeeded) {
      openNewTab(createUrlWithParams(AppRoutes.mintOauthRedirect, { organizationId, productCode }), `session:${productContractId}`);
      onClose();
    }
  };

  // Auto-run once the modal opens and the contract resolves; the button below is a manual retry
  // (e.g. when the auto-run's window.open was blocked). Reset on close so reopening re-runs.
  useEffect(() => {
    if (!isOpen) {
      hasAutoRunRef.current = false;
      return;
    }
    if (!contract || hasAutoRunRef.current) return;
    hasAutoRunRef.current = true;
    handleSetup(true);
  }, [isOpen, contract]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      enableFooter={false}
      width={660}
      title={
        <Typography variant="head20Bold" color={text.primary} width="100%" textAlign="center">
          初回ログインの処理中..
        </Typography>
      }
    >
      {!contract || !me || !organization || isLoadingOrg ? (
        <LoadingArea />
      ) : (
        <Stack gap="32px" pt="8px">
          <Box display="grid" gridTemplateColumns="1fr 1fr" rowGap="16px" columnGap="24px">
            <FieldItem label="組織ID">
              <Input nonControl readonly size="lg" value={organization.externalId ?? ""} />
            </FieldItem>
            <FieldItem label="組織名">
              <Input nonControl readonly size="lg" value={organization.name ?? ""} />
            </FieldItem>
            <Box gridColumn="1 / -1">
              <FieldItem label="対象プロダクト">
                <Input nonControl readonly size="lg" value={contract.product?.name ?? ""} />
              </FieldItem>
            </Box>
            <FieldItem label="氏名">
              <Input nonControl readonly size="lg" value={me.data.fullName ?? ""} />
            </FieldItem>
            <FieldItem label="メールアドレス">
              <Input nonControl readonly size="lg" value={me.data.email ?? ""} />
            </FieldItem>
          </Box>

          <Typography variant="body14" color={text.secondary} pt="16px" component="div">
            管理ユーザーを作成しています。自動で遷移しない場合は、以下からログインしてください。
          </Typography>

          <Stack gap={2} pt="32px">
            <Button
              variant="secondary"
              fullWidth
              isLoading={isProcessing}
              disabled={!contract || isProcessing}
              onClick={() => handleSetup(false)}
              sx={{ height: "48px !important" }}
            >
              <Typography variant="body14Semi">{contract.product?.name} へ移動</Typography>
            </Button>
            <Button variant="ghost" fullWidth onClick={onClose} sx={{ height: "48px !important" }}>
              <Typography variant="body14Semi">戻る</Typography>
            </Button>
          </Stack>
        </Stack>
      )}
    </Modal>
  );
};

const FieldItem: FCC<{ label: string }> = ({ label, children }) => (
  <Stack gap="4px">
    <Typography variant="cap12" color={text.primary}>
      {label}
    </Typography>
    {children}
  </Stack>
);
